import React, { useState, useCallback, useContext, useRef, useEffect } from "react";
import { NodeResizer, useNodeId } from "@xyflow/react";
import { UpdateCanvasLabelContext } from "./UpdateCanvasLabelContext";
import { ResizeUndoContext } from "./ResizeUndoContext";

function labelColor(varName) {
  return {
    fill: `color-mix(in oklab, var(${varName}) 12%, white)`,
    selectedFill: `color-mix(in oklab, var(${varName}) 6%, white)`,
    border: `var(${varName})`,
  };
}

const LABEL_PALETTE = [
  labelColor("--dusty-blue"),
  labelColor("--soft-purple"),
  labelColor("--dusty-teal"),
  labelColor("--muted-sage"),
  labelColor("--warm-gold"),
  labelColor("--brick-red"),
];

function resolveLabelColors(color) {
  return LABEL_PALETTE[color] || LABEL_PALETTE[0];
}

export { LABEL_PALETTE, resolveLabelColors };

export default function CanvasLabelNode({ data, selected }) {
  const { canvasLabel } = data;
  const nodeId = useNodeId();
  const updateCanvasLabel = useContext(UpdateCanvasLabelContext);
  const resizeUndo = useContext(ResizeUndoContext);
  const colors = resolveLabelColors(canvasLabel.color);

  const [editingLabel, setEditingLabel] = useState(false);
  const [editLabelValue, setEditLabelValue] = useState(canvasLabel.label);
  const labelRef = useRef(null);

  const [editingDesc, setEditingDesc] = useState(false);
  const [editDescValue, setEditDescValue] = useState(canvasLabel.description || "");
  const descRef = useRef(null);

  useEffect(() => {
    if (editingLabel && labelRef.current) {
      labelRef.current.focus();
      labelRef.current.select();
    }
  }, [editingLabel]);

  useEffect(() => {
    if (editingDesc && descRef.current) {
      descRef.current.focus();
      descRef.current.select();
    }
  }, [editingDesc]);

  const commitLabel = useCallback(() => {
    const trimmed = editLabelValue.trim();
    if (trimmed && trimmed !== canvasLabel.label && updateCanvasLabel) {
      updateCanvasLabel(nodeId, { label: trimmed });
    }
    setEditingLabel(false);
  }, [editLabelValue, canvasLabel.label, updateCanvasLabel, nodeId]);

  const commitDesc = useCallback(() => {
    const trimmed = editDescValue.trim();
    if (trimmed !== (canvasLabel.description || "") && updateCanvasLabel) {
      updateCanvasLabel(nodeId, { description: trimmed || null });
    }
    setEditingDesc(false);
  }, [editDescValue, canvasLabel.description, updateCanvasLabel, nodeId]);

  const handleLabelKeyDown = useCallback((e) => {
    if (e.key === "Enter") commitLabel();
    else if (e.key === "Escape") { setEditLabelValue(canvasLabel.label); setEditingLabel(false); }
  }, [commitLabel, canvasLabel.label]);

  const handleDescKeyDown = useCallback((e) => {
    if (e.key === "Enter") commitDesc();
    else if (e.key === "Escape") { setEditDescValue(canvasLabel.description || ""); setEditingDesc(false); }
  }, [commitDesc, canvasLabel.description]);

  return (
    <div
      className="canvas-label-node"
      style={{
        width: "100%",
        height: "100%",
        background: selected ? colors.selectedFill : colors.fill,
        border: `2px dashed ${colors.border}`,
        borderRadius: 8,
        padding: "8px 12px",
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={100}
        minHeight={60}
        lineStyle={{ borderColor: colors.border }}
        handleStyle={{ backgroundColor: colors.border, width: 8, height: 8 }}
        onResizeStart={resizeUndo?.onResizeStart}
        onResizeEnd={resizeUndo?.onResizeEnd}
      />
      <div className="canvas-label-node-content">
        {editingLabel ? (
          <input
            ref={labelRef}
            className="canvas-label-node-input canvas-label-node-input--label"
            value={editLabelValue}
            onChange={(e) => setEditLabelValue(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={handleLabelKeyDown}
          />
        ) : (
          <div
            className="canvas-label-node-label"
            style={{ color: colors.border }}
            onDoubleClick={() => { setEditLabelValue(canvasLabel.label); setEditingLabel(true); }}
            title="Double-click to edit"
          >
            {canvasLabel.label}
          </div>
        )}
        {editingDesc ? (
          <input
            ref={descRef}
            className="canvas-label-node-input canvas-label-node-input--desc"
            value={editDescValue}
            onChange={(e) => setEditDescValue(e.target.value)}
            onBlur={commitDesc}
            onKeyDown={handleDescKeyDown}
            placeholder="Add description..."
          />
        ) : (
          <div
            className="canvas-label-node-description"
            onDoubleClick={() => { setEditDescValue(canvasLabel.description || ""); setEditingDesc(true); }}
            title="Double-click to edit"
          >
            {canvasLabel.description || ""}
          </div>
        )}
      </div>
    </div>
  );
}
